import { alpha } from '@mui/material/styles';
import { Paper } from '@mui/material';

// Common button styles used across the application
export const buttonStyles = {
  // Standard action buttons
  actionButton: {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    px: 3,
    py: 1
  },
  
  // Primary action buttons (Create, Save, etc.)
  primaryButton: {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    px: 3,
    py: 1,
    minWidth: 120
  },
  
  // Secondary action buttons (Edit, View, etc.)
  secondaryButton: {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    px: 2,
    py: 0.5,
    minWidth: 80
  },
  
  // Small buttons for compact spaces
  smallButton: {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.7rem',
    py: 0.5,
    px: 1,
    minWidth: 70
  },
  
  // Back navigation buttons
  backButton: {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 500,
    px: 3,
    py: 1,
    minWidth: 140
  },
  
  // Icon buttons with consistent styling
  iconButton: {
    borderRadius: 1,
    p: 0.5
  }
};

// Common form control styles
export const formControlStyles = {
  // Standard form controls (selects, text fields)
  standard: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'background.paper',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  },
  
  // Small form controls for filters
  small: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'background.paper',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    }
  }
};

// Common card styles
export const cardStyles = {
  // Standard elevated cards
  elevated: {
    elevation: 2,
    sx: { mb: 3 }
  },
  
  // Filter cards
  filter: {
    elevation: 2,
    sx: { mb: 3 }
  },
  
  // Summary cards with gradient background
  summary: (theme) => ({
    elevation: 0,
    sx: {
      mb: 3,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
    }
  })
};

// Common chip styles for filters and status indicators
export const chipStyles = {
  // Filter chips
  filter: {
    size: 'small',
    variant: 'outlined',
    sx: {
      borderRadius: 2,
      fontWeight: 500
    }
  },
  
  // Status chips
  status: {
    size: 'small',
    variant: 'filled',
    sx: {
      borderRadius: 2,
      fontWeight: 500,
      fontSize: '0.7rem',
      height: 20
    }
  },
  
  // Company/location chips
  metadata: {
    size: 'small',
    variant: 'outlined',
    sx: {
      fontSize: '0.7rem',
      height: 20,
      borderRadius: 2
    }
  }
};

// Common dialog styles
export const dialogStyles = {
  // Standard dialogs
  standard: {
    '& .MuiDialogActions-root': {
      p: 3,
      gap: 2
    }
  },
  
  // Large dialogs
  large: {
    maxWidth: 'lg',
    fullWidth: true,
    '& .MuiDialogActions-root': {
      p: 3,
      gap: 2
    }
  }
};

// Common table styles
export const tableStyles = {
  // Standard table container
  container: {
    component: Paper,
    variant: 'outlined'
  },
  
  // Compact tables
  compact: {
    size: 'small'
  }
};

// Common layout styles
export const layoutStyles = {
  // Page headers
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3
  },
  
  // Filter sections
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 2,
    flexWrap: 'wrap'
  },
  
  // Action button groups
  actionGroup: {
    display: 'flex',
    gap: 2,
    flexWrap: 'wrap',
    alignItems: 'center'
  }
};

// Utility function to merge styles
export const mergeStyles = (...styles) => {
  return styles.reduce((acc, style) => ({
    ...acc,
    ...style,
    sx: {
      ...acc.sx,
      ...style.sx
    }
  }), {});
};

// Color utilities for consistent theming
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'complete':
    case 'success':
      return 'success';
    case 'rejected':
    case 'error':
    case 'failed':
      return 'error';
    case 'under review':
    case 'pending':
    case 'warning':
      return 'warning';
    case 'draft':
    case 'info':
      return 'info';
    default:
      return 'default';
  }
};

// Common animation styles
export const animationStyles = {
  fadeIn: {
    animation: 'fadeIn 0.3s ease-in-out'
  },
  
  slideIn: {
    animation: 'slideIn 0.3s ease-in-out'
  }
};
